import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  assignOperatorPlan,
  clearPickerSession,
  getOperatorWorkspace,
  listOperatorWorkspaces,
  openOperatorWorkspace,
  readPickerSession,
  setOperatorPlansVisibility,
  setOperatorSuspension,
  updateOperatorCharge,
} from '../services/workspaceService';
import type { OperatorPlanOption, OperatorWorkspace, OperatorWorkspaceDetail } from '../types/workspace';
import { followWorkspaceSelection } from '../workspaceNavigation';
import styles from './OperatorConsole.module.css';

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const dateInputValue = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

type DateSort = 'created' | 'access';

const billingStatusClass = (status: string): string => {
  switch (status) {
    case 'active':
      return styles.badgeActive;
    case 'trial':
      return styles.badgeTrial;
    case 'trialEnded':
      return styles.badgeTrialEnded;
    case 'pastDue':
      return styles.badgePastDue;
    default:
      return styles.badgeMuted;
  }
};

const chargeStatusClass = (status: string): string => {
  switch (status) {
    case 'paid':
      return styles.badgeActive;
    case 'pending':
      return styles.badgeTrialEnded;
    case 'failed':
      return styles.badgePastDue;
    default:
      return styles.badgeMuted;
  }
};

const accessClass = (value: string | null): string => {
  if (!value) {
    return styles.muted;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return styles.muted;
  }

  const days = (parsed.getTime() - Date.now()) / 86_400_000;
  if (days < 0) {
    return styles.dateOverdue;
  }

  if (days <= 7) {
    return styles.dateSoon;
  }

  return styles.dateOk;
};

export const OperatorConsolePage: React.FC = () => {
  const { t } = useTranslation('workspaces');
  const navigate = useNavigate();
  const session = React.useMemo(() => readPickerSession(), []);
  const [search, setSearch] = React.useState('');
  const [appliedSearch, setAppliedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<DateSort>('created');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [items, setItems] = React.useState<OperatorWorkspace[]>([]);
  const [plans, setPlans] = React.useState<OperatorPlanOption[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<OperatorWorkspaceDetail | null>(null);
  const [planCode, setPlanCode] = React.useState('');
  const [accessUntil, setAccessUntil] = React.useState('');
  const [invoiceAmount, setInvoiceAmount] = React.useState('');
  const [invoiceNote, setInvoiceNote] = React.useState('');
  const [invoicePaid, setInvoicePaid] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const selected = items.find(item => item.tenantId === selectedId) ?? null;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const replaceItem = (next: OperatorWorkspace) => {
    setItems(current => current.map(item => (item.tenantId === next.tenantId ? next : item)));
  };

  React.useEffect(() => {
    if (!session?.accountToken || !session.isPlatformOperator) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listOperatorWorkspaces(session.accountToken, appliedSearch, page, sort, sortDir)
      .then(result => {
        if (cancelled) {
          return;
        }

        setItems(result.items);
        setPlans(result.plans);
        setTotalCount(result.totalCount);
        setPageSize(result.pageSize || 25);
        setSelectedId(current => (
          current && result.items.some(item => item.tenantId === current)
            ? current
            : result.items[0]?.tenantId ?? null
        ));
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appliedSearch, page, session?.accountToken, session?.isPlatformOperator, sort, sortDir, t]);

  React.useEffect(() => {
    if (!session?.accountToken || !selectedId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    getOperatorWorkspace(session.accountToken, selectedId)
      .then(result => {
        if (!cancelled) {
          setDetail(result);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId, session?.accountToken, t]);

  React.useEffect(() => {
    if (!selected) {
      return;
    }

    setPlanCode(selected.planCode ?? plans[0]?.code ?? '');
    setAccessUntil(dateInputValue(selected.accessUntilUtc));
    setReason(selected.suspensionReason ?? '');
    setInvoiceAmount('');
    setInvoiceNote('');
    setInvoicePaid(false);
  }, [selected, plans]);

  if (!session?.accountToken || !session.isPlatformOperator) {
    return <Navigate to={session ? '/workspaces' : '/login'} replace />;
  }

  const accountToken = session.accountToken;

  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search);
  };

  const onClearSearch = () => {
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  };

  const onSort = (next: DateSort) => {
    setPage(1);
    if (sort === next) {
      setSortDir(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSort(next);
    setSortDir(next === 'access' ? 'asc' : 'desc');
  };

  const onSavePlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) {
      return;
    }

    const amount = invoiceAmount.trim();
    const parsedAmount = amount ? Number(amount) : null;
    if (parsedAmount !== null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      setError(t('errors.operatorFailed'));
      return;
    }

    setBusy('plan');
    setError(null);
    try {
      const next = await assignOperatorPlan(
        accountToken,
        selected.tenantId,
        planCode,
        accessUntil,
        parsedAmount === null ? undefined : { amountUsd: parsedAmount, note: invoiceNote, paid: invoicePaid },
      );
      replaceItem(next.workspace);
      setDetail(next);
      setInvoiceAmount('');
      setInvoiceNote('');
      setInvoicePaid(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onPlansVisibility = async (hidden: boolean) => {
    if (!selected) {
      return;
    }

    setBusy('plans');
    setError(null);
    try {
      setDetail(await setOperatorPlansVisibility(accountToken, selected.tenantId, hidden));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onChargeStatus = async (orderId: string, status: 'paid' | 'canceled') => {
    if (!selected) {
      return;
    }

    setBusy(`charge:${orderId}`);
    setError(null);
    try {
      setDetail(await updateOperatorCharge(accountToken, selected.tenantId, orderId, status));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onSuspension = async (suspended: boolean) => {
    if (!selected) {
      return;
    }

    setBusy(suspended ? 'suspend' : 'restore');
    setError(null);
    try {
      replaceItem(await setOperatorSuspension(accountToken, selected.tenantId, suspended, reason));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.operatorFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onOpen = async () => {
    if (!selected) {
      return;
    }

    setBusy('open');
    setError(null);
    try {
      const selection = await openOperatorWorkspace(accountToken, selected.tenantId, session.token);
      followWorkspaceSelection(selection, navigate);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.selectFailed'));
      setBusy(null);
    }
  };

  const onSignOut = () => {
    clearPickerSession();
    navigate('/login', { replace: true });
  };

  const sortMark = (column: DateSort): string => {
    if (sort !== column) {
      return '';
    }

    return sortDir === 'asc' ? '↑' : '↓';
  };

  return (
    <main className={styles.page}>
      <div className={styles.frame}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('operator.title')}</h1>
            <p className={styles.subtitle}>{t('operator.subtitle', { email: session.username })}</p>
          </div>
          <div className={styles.headerActions}>
            <button type="button" className={styles.linkButton} onClick={() => navigate('/workspaces')}>
              {t('operator.backToPicker')}
            </button>
            <button type="button" className={styles.linkButton} onClick={onSignOut}>
              {t('operator.signOut')}
            </button>
          </div>
        </header>

        <form className={styles.search} onSubmit={onSearch}>
          <label htmlFor="operator-search">{t('operator.searchLabel')}</label>
          <div className={styles.searchRow}>
            <input
              id="operator-search"
              value={search}
              placeholder={t('operator.searchPlaceholder')}
              onChange={event => setSearch(event.target.value)}
            />
            <button type="submit" className={styles.secondary}>{t('operator.searchAction')}</button>
            {appliedSearch ? (
              <button type="button" className={styles.linkButton} onClick={onClearSearch}>
                {t('operator.clearSearch')}
              </button>
            ) : null}
          </div>
        </form>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.layout}>
          <section className={styles.panel}>
            {!loading ? (
              <div className={styles.panelHead}>
                <span>{t('operator.resultCount', { count: totalCount })}</span>
              </div>
            ) : null}
            {loading ? <p className={styles.status}>{t('operator.loading')}</p> : null}
            {!loading && items.length === 0 ? <p className={styles.status}>{t('operator.empty')}</p> : null}
            {items.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('operator.columns.workspace')}</th>
                    <th>{t('operator.columns.owner')}</th>
                    <th>{t('operator.columns.plan')}</th>
                    <th aria-sort={sort === 'created' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        className={`${styles.sortButton} ${sort === 'created' ? styles.sortButtonActive : ''}`}
                        onClick={() => onSort('created')}
                      >
                        {t('operator.columns.created')}
                        <span aria-hidden="true">{sortMark('created')}</span>
                      </button>
                    </th>
                    <th aria-sort={sort === 'access' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        className={`${styles.sortButton} ${sort === 'access' ? styles.sortButtonActive : ''}`}
                        onClick={() => onSort('access')}
                      >
                        {t('operator.columns.access')}
                        <span aria-hidden="true">{sortMark('access')}</span>
                      </button>
                    </th>
                    <th>{t('operator.columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(workspace => (
                    <tr
                      key={workspace.tenantId}
                      className={`${styles.row} ${workspace.tenantId === selectedId ? styles.rowSelected : ''} ${workspace.isSuspended ? styles.rowSuspended : ''}`}
                      onClick={() => setSelectedId(workspace.tenantId)}
                    >
                      <td>
                        <div className={styles.name}>{workspace.name}</div>
                        <div className={styles.muted}>{hostOf(workspace.url) || workspace.slug}</div>
                      </td>
                      <td>
                        <div>{workspace.ownerName || workspace.ownerEmail || t('operator.noOwner')}</div>
                        {workspace.ownerName && workspace.ownerEmail ? (
                          <div className={styles.muted}>{workspace.ownerEmail}</div>
                        ) : null}
                      </td>
                      <td>
                        <span className={workspace.planCode ? styles.planPill : styles.muted}>
                          {workspace.planCode || t('operator.noPlan')}
                        </span>
                      </td>
                      <td className={styles.muted}>{formatDate(workspace.createdAtUtc) || t('operator.noDate')}</td>
                      <td className={accessClass(workspace.accessUntilUtc)}>
                        {formatDate(workspace.accessUntilUtc) || t('operator.noDate')}
                      </td>
                      <td>
                        <div className={styles.badges}>
                          <span className={billingStatusClass(workspace.billingStatus)}>
                            {t(`operator.status.${workspace.billingStatus}`, { defaultValue: workspace.billingStatus })}
                          </span>
                          {workspace.isSuspended ? (
                            <span className={styles.badgeSuspended}>{t('operator.suspended')}</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {totalCount > pageSize ? (
              <div className={styles.pager}>
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={page <= 1 || busy !== null}
                  onClick={() => setPage(current => Math.max(1, current - 1))}
                >
                  {t('operator.previous')}
                </button>
                <span className={styles.muted}>{t('operator.pageOf', { page, pages: pageCount })}</span>
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={page >= pageCount || busy !== null}
                  onClick={() => setPage(current => current + 1)}
                >
                  {t('operator.next')}
                </button>
              </div>
            ) : null}
          </section>

          <aside className={`${styles.panel} ${styles.detailPanel}`}>
            {selected ? (
              <div className={styles.detail}>
                <div>
                  <h2>{selected.name}</h2>
                  <p className={styles.muted}>{hostOf(selected.url) || selected.slug}</p>
                  <div className={styles.badges}>
                    <span className={billingStatusClass(selected.billingStatus)}>
                      {t(`operator.status.${selected.billingStatus}`, { defaultValue: selected.billingStatus })}
                    </span>
                    {selected.isSuspended ? (
                      <span className={styles.badgeSuspended}>{t('operator.suspended')}</span>
                    ) : null}
                  </div>
                  {selected.suspensionReason ? <p className={styles.reason}>{selected.suspensionReason}</p> : null}
                </div>
                <h3 className={styles.sectionTitle}>{t('operator.planSection')}</h3>
                <form className={styles.form} onSubmit={event => void onSavePlan(event)}>
                  <label className={styles.field}>
                    {t('operator.planLabel')}
                    <select value={planCode} onChange={event => setPlanCode(event.target.value)} disabled={busy !== null}>
                      {plans.map(plan => (
                        <option key={plan.code} value={plan.code}>{plan.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.field}>
                    {t('operator.accessUntilLabel')}
                    <input
                      type="date"
                      value={accessUntil}
                      required
                      disabled={busy !== null}
                      onChange={event => setAccessUntil(event.target.value)}
                    />
                  </label>
                  <label className={styles.field}>
                    {t('operator.invoiceAmount')}
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={invoiceAmount}
                      placeholder={t('operator.invoiceAmountPlaceholder')}
                      disabled={busy !== null}
                      onChange={event => setInvoiceAmount(event.target.value)}
                    />
                  </label>
                  <label className={styles.field}>
                    {t('operator.invoiceNote')}
                    <input
                      value={invoiceNote}
                      placeholder={t('operator.invoiceNotePlaceholder')}
                      disabled={busy !== null}
                      onChange={event => setInvoiceNote(event.target.value)}
                    />
                  </label>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={invoicePaid}
                      disabled={busy !== null}
                      onChange={event => setInvoicePaid(event.target.checked)}
                    />
                    {t('operator.invoicePaid')}
                  </label>
                  <div className={styles.actions}>
                    <button type="submit" className={styles.primary} disabled={busy !== null || !planCode || !accessUntil}>
                      {busy === 'plan' ? t('operator.saving') : t('operator.savePlan')}
                    </button>
                  </div>
                </form>
                <h3 className={styles.sectionTitle}>{t('operator.suspensionSection')}</h3>
                <label className={styles.field}>
                  {t('operator.reasonLabel')}
                  <textarea
                    rows={3}
                    value={reason}
                    placeholder={t('operator.reasonPlaceholder')}
                    disabled={busy !== null}
                    onChange={event => setReason(event.target.value)}
                  />
                </label>
                <div className={styles.actions}>
                  {selected.isSuspended ? (
                    <button
                      type="button"
                      className={styles.secondary}
                      disabled={busy !== null}
                      onClick={() => void onSuspension(false)}
                    >
                      {busy === 'restore' ? t('operator.saving') : t('operator.restore')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.danger}
                      disabled={busy !== null || reason.trim().length === 0}
                      onClick={() => void onSuspension(true)}
                    >
                      {busy === 'suspend' ? t('operator.saving') : t('operator.suspend')}
                    </button>
                  )}
                  <button type="button" className={styles.secondary} disabled={busy !== null} onClick={() => void onOpen()}>
                    {busy === 'open' ? t('operator.opening') : t('operator.open')}
                  </button>
                </div>
                {detail?.workspace.tenantId === selected.tenantId ? (
                  <>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={detail.plansHidden}
                        disabled={busy !== null}
                        onChange={event => void onPlansVisibility(event.target.checked)}
                      />
                      <span>
                        {t('operator.hidePlans')}
                        <span className={styles.muted}>{t('operator.hidePlansHelp')}</span>
                      </span>
                    </label>
                    <dl className={styles.meta}>
                      <div><dt>{t('operator.createdLabel')}</dt><dd>{formatDate(selected.createdAtUtc) || t('operator.noDate')}</dd></div>
                      <div><dt>{t('operator.provider')}</dt><dd>{detail.provider || t('operator.noPlan')}</dd></div>
                      <div><dt>{t('operator.periodStart')}</dt><dd>{formatDate(detail.periodStartUtc) || t('operator.noDate')}</dd></div>
                      <div><dt>{t('operator.periodEnd')}</dt><dd className={accessClass(detail.periodEndUtc)}>{formatDate(detail.periodEndUtc) || t('operator.noDate')}</dd></div>
                      <div><dt>{t('operator.trialEnds')}</dt><dd className={accessClass(detail.trialEndsAtUtc)}>{formatDate(detail.trialEndsAtUtc) || t('operator.noDate')}</dd></div>
                    </dl>
                    <section className={styles.section}>
                      <h3>{t('operator.people')}</h3>
                      <div className={styles.people}>
                        <div><strong>{detail.people.owners}</strong>{t('operator.owners')}</div>
                        <div><strong>{detail.people.admins}</strong>{t('operator.admins')}</div>
                        <div><strong>{detail.people.members}</strong>{t('operator.members')}</div>
                      </div>
                    </section>
                    <section className={styles.section}>
                      <h3>{t('operator.charges')}</h3>
                      {detail.charges.length === 0 ? <p className={styles.muted}>{t('operator.chargesEmpty')}</p> : (
                        <ul className={styles.list}>
                          {detail.charges.map(charge => (
                            <li key={charge.id}>
                              <strong>
                                {charge.planCode}
                                {' · '}
                                {charge.amountUsd.toFixed(2)}
                                {' USD'}
                              </strong>
                              <span className={styles.chargeMeta}>
                                <span className={chargeStatusClass(charge.status)}>
                                  {t(`operator.chargeStatus.${charge.status}`, { defaultValue: charge.status })}
                                </span>
                                <span className={styles.muted}>
                                  {charge.provider}
                                  {' · '}
                                  {formatDateTime(charge.createdAtUtc)}
                                </span>
                              </span>
                              {charge.note ? <span className={styles.muted}>{charge.note}</span> : null}
                              {charge.provider === 'operator' && (charge.status === 'pending' || charge.status === 'paid') ? (
                                <span className={styles.chargeActions}>
                                  {charge.status === 'pending' ? (
                                    <button
                                      type="button"
                                      className={styles.secondary}
                                      disabled={busy !== null}
                                      onClick={() => void onChargeStatus(charge.id, 'paid')}
                                    >
                                      {busy === `charge:${charge.id}` ? t('operator.saving') : t('operator.markPaid')}
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className={styles.danger}
                                    disabled={busy !== null}
                                    onClick={() => void onChargeStatus(charge.id, 'canceled')}
                                  >
                                    {t('operator.cancelCharge')}
                                  </button>
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                    <section className={styles.section}>
                      <h3>{t('operator.activity')}</h3>
                      {detail.activity.length === 0 ? <p className={styles.muted}>{t('operator.activityEmpty')}</p> : (
                        <ul className={styles.list}>
                          {detail.activity.map(entry => (
                            <li key={`${entry.action}-${entry.createdAtUtc}`}>
                              <strong>{t(`operator.actions.${entry.action}`, { defaultValue: entry.action })}</strong>
                              <span className={styles.muted}>
                                {entry.actorEmail}
                                {' · '}
                                {formatDateTime(entry.createdAtUtc)}
                              </span>
                              {entry.detail ? <span className={styles.muted}>{entry.detail}</span> : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </>
                ) : (
                  <p className={styles.status}>{t('operator.detailLoading')}</p>
                )}
              </div>
            ) : (
              <p className={styles.status}>{t('operator.empty')}</p>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
};

export default OperatorConsolePage;
